/**
 * Close some element when clicked outside of it.
 * - ensure not clicked in some child elements
 */
import type { AfterViewInit } from '@angular/core';
import { DestroyRef, Directive, ElementRef, HostListener, inject, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

@Directive({
  selector: '[evcCloseOnClickOutside]',
  standalone: true,
})
export class CloseOnClickOutsideDirective implements AfterViewInit {
  public readonly clickedOutside = output();

  public enabled = false;
  readonly #elementRef = inject(ElementRef);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);

  @HostListener('document:click', ['$event.target'])
  public onClick(targetElement: EventTarget | null): void {
    if (!(targetElement instanceof HTMLElement)) return;

    const clickedInside = this.#elementRef.nativeElement.contains(targetElement);
    if (clickedInside || !this.enabled) return;

    this.#emitClickedOutside();
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.enabled = true;
    });

    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(this.#emitClickedOutside.bind(this));
  }

  #emitClickedOutside(): void {
    this.clickedOutside.emit();
  }
}
